import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ROUTES } from '../constants/routes';
import { UI, DISPLAY, EASE } from '../styles/tl5';
import heroImg from '../assets/img/v5/partners-hero.jpg';

// Partners — the hub, built to the v5 reference.
//
// Four blocks: the hero, the six partnership cards, a strip of four
// credentials, and the four-step "how partnering works" panel. Nothing
// here talks to the API: the hub is a router into the six partner
// journeys, and every form lives on the page it routes to.
//
// The six cards keep the reference's exact colour rotation — dark, teal,
// white, deep green, white, yellow — which is what stops a grid of six
// near-identical cards reading as a wall.

const PARTNERSHIPS = [
  {
    kicker: 'Doctors & clinics',
    title: 'Refer patients and get reports in the Partner app.',
    cta: 'Sign up as a doctor →',
    to: ROUTES.PARTNER_DOCTORS,
    bg: 'var(--tl-g9)',
    fg: '#FFFFFF',
    kickerFg: 'var(--tl-teal-l)',
    ctaFg: 'var(--tl-yellow)',
  },
  {
    kicker: 'Laboratories & collection centres',
    title: 'Send samples to our NABL lab at partner rates.',
    cta: 'Sign up as a lab →',
    to: ROUTES.PARTNER_LABS,
    bg: 'var(--tl-teal)',
    fg: '#FFFFFF',
    kickerFg: 'var(--tl-g9)',
    ctaFg: 'var(--tl-g9)',
  },
  {
    kicker: 'Corporates',
    title: 'Employee health checks and on-site camps.',
    cta: 'Request a proposal →',
    to: ROUTES.CORPORATE,
    bg: '#FFFFFF',
    fg: 'var(--tl-g9)',
    kickerFg: 'var(--tl-teal-d)',
    ctaFg: 'var(--tl-teal-d)',
  },
  {
    kicker: 'Phlebotomists',
    title: 'Join as a Trustian and collect at patients’ homes.',
    cta: 'Apply as a phlebotomist →',
    to: ROUTES.PARTNER_PHLEBOS,
    bg: 'var(--tl-g7)',
    fg: '#FFFFFF',
    kickerFg: 'var(--tl-teal-l)',
    ctaFg: 'var(--tl-yellow)',
  },
  {
    kicker: 'Managed labs · HLM & independent labs',
    title: 'We set up, upgrade and run your laboratory.',
    cta: 'Talk to us →',
    to: ROUTES.PARTNER_MANAGED_LABS,
    bg: '#FFFFFF',
    fg: 'var(--tl-g9)',
    kickerFg: 'var(--tl-teal-d)',
    ctaFg: 'var(--tl-teal-d)',
  },
  {
    kicker: 'TrustLab franchisees',
    title: 'Open a TrustLab-branded collection centre.',
    cta: 'Apply for a franchise →',
    to: ROUTES.FRANCHISE,
    bg: 'var(--tl-yellow)',
    fg: 'var(--tl-g9)',
    kickerFg: 'var(--tl-g9)',
    ctaFg: 'var(--tl-g9)',
  },
];

const CREDENTIALS = [
  ['NABL', 'ISO 15189:2022 accredited'],
  ['1,765', 'Tests in our Directory of Services'],
  ['In-house', 'Genomics, cytogenetics, flow cytometry, histopathology'],
  ['14', 'Branches across India'],
];

const STEPS = [
  ['01', 'Sign up', 'Tell us who you are. It takes two minutes.'],
  ['02', 'We verify', 'Our partner team checks your registration and calls you.'],
  ['03', 'Account and rate card', 'Partner app access, pricing and pickup schedule.'],
  ['04', 'Start sending', 'Refer patients or send samples from day one.'],
];

const Partners = () => (
  <Box>
    {/* ── hero ───────────────────────────────────────────────────────── */}
    <Box component="section" sx={{ pt: 2, px: 2 }}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 1320,
          mx: 'auto',
          minHeight: { xs: 360, md: 460 },
          display: 'flex',
          alignItems: 'end',
          borderRadius: '36px',
          overflow: 'hidden',
          bgcolor: 'var(--tl-g9)',
        }}
      >
        <Box
          component="img"
          src={heroImg}
          alt=""
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(90deg, rgba(7,33,29,0.94) 0%, rgba(7,33,29,0.76) 52%, rgba(7,33,29,0.25) 100%)',
          }}
        />
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: 860,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            p: 'clamp(28px, 5vw, 56px)',
            color: '#FFFFFF',
          }}
        >
          <Box
            component="span"
            sx={{
              alignSelf: 'start',
              px: '12px',
              py: '7px',
              borderRadius: '999px',
              bgcolor: 'var(--tl-yellow)',
              color: 'var(--tl-g9)',
              fontFamily: UI,
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            Partner with TrustLab
          </Box>

          <Typography
            component="h1"
            sx={{
              m: 0,
              fontFamily: DISPLAY,
              fontWeight: 700,
              fontSize: 'clamp(34px, 5.8vw, 68px)',
              lineHeight: 0.95,
              letterSpacing: '-.035em',
              textWrap: 'balance',
              color: '#FFFFFF',
            }}
          >
            Your patients.{' '}
            <Box component="span" sx={{ color: 'var(--tl-teal-l)' }}>Our NABL lab behind you.</Box>
          </Typography>

          <Typography
            sx={{ m: 0, fontFamily: UI, fontSize: 18, lineHeight: 1.55, color: '#D5E7E2', maxWidth: 620 }}
          >
            For doctors, labs, collection centres, phlebotomists, hospitals and companies.
            Find the partnership that fits, backed by our NABL-accredited lab and 1,765 tests.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
            {/* An in-page anchor, so it needs a plain <a>, not a Link. */}
            <Box
              component="a"
              href="#choose"
              sx={{
                p: '15px 22px',
                borderRadius: '999px',
                bgcolor: 'var(--tl-yellow)',
                color: 'var(--tl-g9)',
                fontFamily: UI,
                fontSize: 16,
                fontWeight: 800,
                textDecoration: 'none',
                '&:hover': { bgcolor: 'var(--tl-yellow-d)', color: 'var(--tl-g9)' },
              }}
            >
              Choose your partnership
            </Box>
            <Box
              component={Link}
              to={ROUTES.LOGIN}
              sx={{
                p: '14px 22px',
                borderRadius: '999px',
                border: '1.5px solid #5A7D75',
                color: '#FFFFFF',
                fontFamily: UI,
                fontSize: 16,
                fontWeight: 800,
                textDecoration: 'none',
                '&:hover': { borderColor: 'var(--tl-teal-l)', color: '#FFFFFF' },
              }}
            >
              Partner sign in
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>

    <Box
      component="main"
      sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 1.5, pb: 9 }}
    >
      {/* ── choose your partnership ──────────────────────────────────── */}
      <Box
        component="section"
        id="choose"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          // Clears the sticky header when the hero button jumps here.
          scrollMarginTop: '100px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 680, p: '8px 8px 0' }}>
          <Box
            component="span"
            sx={{
              fontFamily: UI,
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: 'var(--tl-teal-d)',
            }}
          >
            Choose your partnership
          </Box>
          <Typography
            component="h2"
            sx={{
              m: 0,
              fontFamily: DISPLAY,
              fontWeight: 700,
              fontSize: 'clamp(28px, 3.8vw, 46px)',
              lineHeight: 1.02,
              letterSpacing: '-.03em',
              textWrap: 'balance',
              color: 'var(--tl-g9)',
            }}
          >
            Six ways to work with TrustLab.
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 1.5 }}>
          {PARTNERSHIPS.map((p) => (
            <Box
              key={p.kicker}
              component={Link}
              to={p.to}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                minHeight: 230,
                p: '26px',
                borderRadius: '28px',
                bgcolor: p.bg,
                color: p.fg,
                textDecoration: 'none',
                transition: `transform .35s ${EASE}, box-shadow .35s ${EASE}`,
                '&:hover': {
                  color: p.fg,
                  transform: 'translateY(-4px)',
                  boxShadow: '0 18px 40px -22px rgba(11,43,39,0.55)',
                },
              }}
            >
              <Box component="span" sx={{ fontFamily: UI, fontSize: 13, fontWeight: 800, color: p.kickerFg }}>
                {p.kicker}
              </Box>
              <Box
                component="span"
                sx={{ flex: 1, fontFamily: DISPLAY, fontWeight: 700, fontSize: 24, lineHeight: 1.1, color: p.fg }}
              >
                {p.title}
              </Box>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 15, fontWeight: 800, color: p.ctaFg }}>
                {p.cta}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── credentials ──────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
          gap: 1.5,
          mt: 4.5,
        }}
      >
        {CREDENTIALS.map(([v, l]) => (
          <Box
            key={l}
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: 3, borderRadius: '28px', bgcolor: '#FFFFFF' }}
          >
            <Box
              component="span"
              sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 40, lineHeight: 1, color: 'var(--tl-teal-d)' }}
            >
              {v}
            </Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: 'var(--tl-muted-2)' }}>
              {l}
            </Box>
          </Box>
        ))}
      </Box>

      {/* ── how partnering works ─────────────────────────────────────── */}
      <Box
        component="section"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          mt: 4.5,
          p: 'clamp(24px, 4vw, 48px)',
          borderRadius: '36px',
          bgcolor: '#FFFFFF',
        }}
      >
        <Typography
          component="h2"
          sx={{
            m: 0,
            fontFamily: DISPLAY,
            fontWeight: 700,
            fontSize: 'clamp(28px, 3.8vw, 48px)',
            lineHeight: 1,
            letterSpacing: '-.03em',
            color: 'var(--tl-g9)',
          }}
        >
          How partnering works
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 1.5 }}>
          {STEPS.map(([n, t, d]) => (
            <Box
              key={n}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: '22px', borderRadius: '24px', bgcolor: 'var(--tl-mint)' }}
            >
              <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 15, color: 'var(--tl-teal-d)' }}>
                {n}
              </Box>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 18, fontWeight: 800, lineHeight: 1.25 }}>
                {t}
              </Box>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.55, color: 'var(--tl-muted)' }}>
                {d}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  </Box>
);

export default Partners;
